function _createForOfIteratorHelper(r, e) {
  var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"];
  if (!t) {
    if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) {
      t && (r = t);
      var n = 0,
        F = function () {};
      return {
        s: F,
        n: function () {
          return n >= r.length ? {
            done: !0
          } : {
            done: !1,
            value: r[n++]
          };
        },
        e: function (r) {
          throw r;
        },
        f: F
      };
    }
    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }
  var o,
    a = !0,
    u = !1;
  return {
    s: function () {
      t = t.call(r);
    },
    n: function () {
      var r = t.next();
      return a = r.done, r;
    },
    e: function (r) {
      u = !0, o = r;
    },
    f: function () {
      try {
        a || null == t.return || t.return();
      } finally {
        if (u) throw o;
      }
    }
  };
}
import PropTypes from "prop-types";
function _unsupportedIterableToArray(r, a) {
  if (r) {
    if ("string" == typeof r) return _arrayLikeToArray(r, a);
    var t = {}.toString.call(r).slice(8, -1);
    return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0;
  }
}
function _arrayLikeToArray(r, a) {
  (null == a || a > r.length) && (a = r.length);
  for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e];
  return n;
}
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.string.match.js";
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { createElement, useEffect, useImperativeHandle, useMemo, useState, useCallback, useRef, forwardRef } from 'react';
import unified from 'unified';
import classNames from 'classnames';
import { useEuiMemoizedStyles, useGeneratedHtmlId } from '../../services';
import { EuiModal } from '../modal';
import { EuiResizeObserver } from '../observer/resize_observer';
import MarkdownActions, { insertText } from './markdown_actions';
import { EuiMarkdownEditorToolbar } from './markdown_editor_toolbar';
import { EuiMarkdownEditorTextArea } from './markdown_editor_text_area';
import { EuiMarkdownFormat } from './markdown_format';
import { EuiMarkdownEditorDropZone } from './markdown_editor_drop_zone';
import { MODE_EDITING, MODE_VIEWING } from './markdown_modes';
import { EuiMarkdownContext } from './markdown_context';
import { defaultParsingPlugins, defaultProcessingPlugins, defaultUiPlugins } from './plugins/markdown_default_plugins';
import { euiMarkdownEditorStyles } from './markdown_editor.styles';
// TODO I wanted to use the useCombinedRefs
// but I can't because it's not allowed to use react hooks
// inside a callback.
var mergeRefs = function mergeRefs() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }
  var filteredRefs = refs.filter(Boolean);
  if (!filteredRefs.length) return null;
  if (filteredRefs.length === 0) return filteredRefs[0];
  return function (inst) {
    var _iterator = _createForOfIteratorHelper(filteredRefs),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var ref = _step.value;
        if (typeof ref === 'function') {
          ref(inst);
        } else if (ref) {
          ref.current = inst;
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  };
};
function isNewLine(char) {
  if (char == null) return true;
  return !!char.match(/[\r\n]/);
}
function padWithNewlinesIfNeeded(textarea, text) {
  var selectionStart = textarea.selectionStart;
  var selectionEnd = textarea.selectionEnd;

  // block parsing requires two leading new lines and none trailing, but we add an extra trailing line for readability
  var isPrevNewLine = isNewLine(textarea.value[selectionStart - 1]);
  var isPrevPrevNewLine = isNewLine(textarea.value[selectionStart - 2]);
  var isNextNewLine = isNewLine(textarea.value[selectionEnd]);

  // pad text with newlines as needed
  text = "".concat(isPrevNewLine ? '' : '\n').concat(isPrevPrevNewLine ? '' : '\n').concat(text).concat(isNextNewLine ? '' : '\n');
  return text;
}
export var EuiMarkdownEditor = forwardRef(({
  className,
  editorId: _editorId,
  value,
  onChange,
  height = 250,
  maxHeight = 500,
  autoExpandPreview = true,
  parsingPluginList = defaultParsingPlugins,
  processingPluginList = defaultProcessingPlugins,
  uiPlugins = defaultUiPlugins,
  onParse,
  errors = [],
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  initialViewMode = MODE_EDITING,
  dropHandlers = [],
  markdownFormatProps,
  placeholder,
  readOnly,
  ...rest
}, ref) => {
  const [viewMode, setViewMode] = useState(initialViewMode);
  const editorId = useGeneratedHtmlId({
    conditionalId: _editorId
  });
  const [pluginEditorPlugin, setPluginEditorPlugin] = useState(undefined);
  const toolbarPlugins = [...uiPlugins];
  const markdownActions = useMemo(() => new MarkdownActions(editorId, toolbarPlugins),
  // toolbarPlugins _is_ accounted for
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [editorId, toolbarPlugins.map(({
    name
  }) => name).join(',')]);
  const parser = useMemo(() => {
    const Compiler = tree => {
      return tree;
    };
    function identityCompiler() {
      this.Compiler = Compiler;
    }
    return unified().use(parsingPluginList).use(identityCompiler);
  }, [parsingPluginList]);
  const [parsed, parseError] = useMemo(() => {
    try {
      const parsed = parser.processSync(value);
      return [parsed, null];
    } catch (e) {
      return [null, e];
    }
  }, [parser, value]);
  const isPreviewing = viewMode === MODE_VIEWING;
  const isEditing = viewMode === MODE_EDITING;
  const replaceNode = useCallback((position, next) => {
    const leading = value.substring(0, position.start.offset);
    const trailing = value.substring(position.end.offset);
    onChange(`${leading}${next}${trailing}`);
  }, [value, onChange]);
  const contextValue = useMemo(() => ({
    openPluginEditor: readOnly ? () => {} : plugin => setPluginEditorPlugin(() => plugin),
    replaceNode: readOnly ? () => {} : replaceNode,
    readOnly: readOnly
  }), [replaceNode, readOnly]);
  const [selectedNode, setSelectedNode] = useState();
  const textareaRef = useRef(null);
  useEffect(() => {
    if (textareaRef == null) return;
    if (parsed == null) return;
    const getCursorNode = () => {
      const {
        selectionStart
      } = textareaRef.current;
      let node = parsed.result ?? parsed.contents;
      outer: while (true) {
        if (node.children) {
          for (let i = 0; i < node.children.length; i++) {
            const child = node.children[i];
            if (child.position && child.position.start.offset < selectionStart && selectionStart < child.position.end.offset) {
              if (child.type === 'text') break outer; // don't dive into `text` nodes
              node = child;
              continue outer;
            }
          }
        }
        break;
      }
      setSelectedNode(node);
    };
    // `parsed` changed, which means the node at the cursor may be different
    // e.g. from clicking a toolbar button
    getCursorNode();
    const textarea = textareaRef.current;
    textarea.addEventListener('keyup', getCursorNode);
    textarea.addEventListener('mouseup', getCursorNode);
    return () => {
      textarea.removeEventListener('keyup', getCursorNode);
      textarea.removeEventListener('mouseup', getCursorNode);
    };
  }, [parsed]);
  useEffect(() => {
    if (onParse) {
      const messages = parsed ? parsed.messages : [];
      const ast = parsed ? parsed.result ?? parsed.contents : null;
      onParse(parseError, {
        messages,
        ast
      });
    }
  }, [onParse, parsed, parseError]);
  useImperativeHandle(ref, () => ({
    textarea: textareaRef.current,
    replaceNode
  }), [replaceNode]);
  const textarea = textareaRef.current;
  const previewRef = useRef(null);
  const editorToolbarRef = useRef(null);
  const [hasUnacceptedItems, setHasUnacceptedItems] = React.useState(false);
  const [currentHeight, setCurrentHeight] = useState(height);
  const [editorFooterHeight, setEditorFooterHeight] = useState(0);
  const [editorToolbarHeight, setEditorToolbarHeight] = useState(0);
  const styles = useEuiMemoizedStyles(euiMarkdownEditorStyles);
  const cssStyles = [styles.euiMarkdownEditor, height === 'full' && styles.fullHeight];
  const classes = classNames('euiMarkdownEditor', {
    'euiMarkdownEditor-isPreviewing': isPreviewing
  }, className);
  const classesPreview = classNames('euiMarkdownEditorPreview', {
    'euiMarkdownEditorPreview-isReadOnly': readOnly
  });
  const onResize = () => {
    if (textarea && isEditing && height !== 'full') {
      const resizedTextareaHeight = textarea.offsetHeight + editorFooterHeight;
      setCurrentHeight(resizedTextareaHeight);
    }
  };
  useEffect(() => {
    setEditorToolbarHeight(editorToolbarRef.current.offsetHeight);
  }, [setEditorToolbarHeight]);
  useEffect(() => {
    if (height === 'full' || currentHeight === 'full') return;
    if (isPreviewing && autoExpandPreview && previewRef.current.scrollHeight > currentHeight) {
      // scrollHeight does not include the border or margin
      // so we ask for the computed value for those,
      // which is always in pixels because getComputedValue
      // returns the resolved values
      const elementComputedStyle = window.getComputedStyle(previewRef.current);
      const borderWidth = parseFloat(elementComputedStyle.borderTopWidth) + parseFloat(elementComputedStyle.borderBottomWidth);
      const marginWidth = parseFloat(elementComputedStyle.marginTop) + parseFloat(elementComputedStyle.marginBottom);

      // then add an extra pixel for safety and because the scrollHeight value is rounded
      const extraHeight = borderWidth + marginWidth + 1;
      setCurrentHeight(previewRef.current.scrollHeight + extraHeight);
    }
  }, [currentHeight, isPreviewing, height, autoExpandPreview]);
  const previewHeight = height === 'full' ? `calc(100% - ${editorFooterHeight}px)` : currentHeight;
  const textAreaHeight = height === 'full' ? '100%' : `calc(${height - editorFooterHeight}px)`;
  const textAreaMaxHeight = height !== 'full' ? `${maxHeight - editorFooterHeight}px` : '';

  // safari needs this calc when the height is set to full
  const editorToggleContainerHeight = `calc(100% - ${editorToolbarHeight}px)`;
  return <EuiMarkdownContext.Provider value={contextValue}>
        <div className={classes} css={cssStyles} {...rest}>
          <EuiMarkdownEditorToolbar ref={editorToolbarRef} selectedNode={selectedNode} markdownActions={markdownActions} onClickPreview={() => setViewMode(isPreviewing ? MODE_EDITING : MODE_VIEWING)} viewMode={viewMode} uiPlugins={toolbarPlugins} />

          {isPreviewing && <div ref={previewRef} className={classesPreview} css={styles.euiMarkdownEditorPreview} style={{
        height: previewHeight
      }}>
              <EuiMarkdownFormat parsingPluginList={parsingPluginList} processingPluginList={processingPluginList} {...markdownFormatProps}>
                {value}
              </EuiMarkdownFormat>
            </div>}
          {/* Toggle the editor's display instead of unmounting to retain its undo/redo history */}
          <div className="euiMarkdownEditor__toggleContainer" style={{
        height: editorToggleContainerHeight,
        display: isPreviewing ? 'none' : undefined
      }}>
            <EuiMarkdownEditorDropZone setEditorFooterHeight={setEditorFooterHeight} isEditing={isEditing} dropHandlers={dropHandlers} insertText={(text, config) => {
          if (config.block) {
            text = padWithNewlinesIfNeeded(textareaRef.current, text);
          }
          const originalSelectionStart = textareaRef.current.selectionStart;
          const newSelectionPoint = originalSelectionStart + text.length;
          insertText(textareaRef.current, {
            text,
            selectionStart: newSelectionPoint,
            selectionEnd: newSelectionPoint
          });
        }} uiPlugins={toolbarPlugins} errors={errors} hasUnacceptedItems={hasUnacceptedItems} setHasUnacceptedItems={setHasUnacceptedItems}>
              <EuiResizeObserver onResize={onResize}>
                {resizeRef => {
              return <EuiMarkdownEditorTextArea height={textAreaHeight} maxHeight={textAreaMaxHeight} ref={mergeRefs(textareaRef, resizeRef)} id={editorId} onChange={e => onChange(e.target.value)} value={value} onFocus={() => setHasUnacceptedItems(false)} placeholder={placeholder} readOnly={readOnly} {...{
                'aria-label': ariaLabel,
                'aria-labelledby': ariaLabelledBy,
                'aria-describedby': ariaDescribedBy
              }} />;
            }}
              </EuiResizeObserver>
            </EuiMarkdownEditorDropZone>

            {pluginEditorPlugin && <EuiModal onClose={() => setPluginEditorPlugin(undefined)}>
                {createElement(pluginEditorPlugin.editor, {
            node: selectedNode && selectedNode.type === pluginEditorPlugin.name ? selectedNode : null,
            onCancel: () => setPluginEditorPlugin(undefined),
            onSave: (markdown, config) => {
              if (selectedNode && selectedNode.type === pluginEditorPlugin.name && selectedNode.position) {
                // modifying an existing node
                textareaRef.current.setSelectionRange(selectedNode.position.start.offset, selectedNode.position.end.offset);
              } else {
                // creating a new node
                if (config.block) {
                  // inject newlines if needed
                  markdown = padWithNewlinesIfNeeded(textareaRef.current, markdown);
                }
              }
              insertText(textareaRef.current, {
                text: markdown,
                selectionStart: undefined,
                selectionEnd: undefined
              });
              setPluginEditorPlugin(undefined);
            }
          })}
              </EuiModal>}
          </div>
        </div>
      </EuiMarkdownContext.Provider>;
});
EuiMarkdownEditor.propTypes = {
  className: PropTypes.string,
  /** aria-label OR aria-labelledby must be set */"aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /** aria-label OR aria-labelledby must be set */"aria-labelledby": PropTypes.string,
  /** ID of an element describing the text editor, useful for associating error messages */"aria-describedby": PropTypes.string,
  /** a unique ID to attach to the textarea. If one isn't provided, a random one
       * will be generated */
  editorId: PropTypes.string,
  /** A markdown content */value: PropTypes.string.isRequired,
  /** callback function when markdown content is modified */onChange: PropTypes.func.isRequired,
  /**
       * Sets the current display mode to a read-only state. All editing gets resctricted.
       */
  readOnly: PropTypes.bool,
  /**
       * Sets the `height` in pixels of the editor/preview area or pass `full` to allow
       * the EuiMarkdownEditor to fill the height of its container.
       * When in `full` mode the vertical resize is not allowed.
       */
  height: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.oneOf(["full"])]),
  /**
       * Sets the `max-height` in pixels of the editor/preview area.
       * It has no effect when the `height` is set to `full`.
       */
  maxHeight: PropTypes.number,
  /**
       * Automatically adjusts the preview height to fit all the content and avoid a scrollbar.
       */
  autoExpandPreview: PropTypes.bool,
  /** plugins to identify new syntax and parse it into an AST node */parsingPluginList: PropTypes.any,
  /** plugins to process the markdown AST nodes into a React nodes */processingPluginList: PropTypes.any,
  /** defines UI for plugins' buttons in the toolbar as well as any modals or extra UI that provides content to the editor */uiPlugins: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    button: PropTypes.shape({
      label: PropTypes.string.isRequired,
      iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "analyzeEvent", "annotation", "anomalyChart", "anomalySwimLane", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "article", "asterisk", "at", "auditbeatApp", "beaker", "bell", "bellSlash", "beta", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "changePointDetection", "check", "checkInCircleFilled", "cheer", "classificationJob", "clickLeft", "clickRight", "clock", "clockCounter", "cloudDrizzle", "cloudStormy", "cloudSunny", "cluster", "codeApp", "color", "compute", "console", "consoleApp", "container", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crossInCircle", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "diff", "discoverApp", "discuss", "document", "documentEdit", "documentation", "documents", "dot", "dotInCircle", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "endpoint", "eql", "eraser", "error", "errorFilled", "esqlVis", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "fieldStatistics", "filebeatApp", "filter", "filterExclude", "filterIgnore", "filterInclude", "filterInCircle", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "grabOmnidirectional", "gradient", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "index", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "indexTemporary", "infinity", "inputOutput", "inspect", "invert", "ip", "key", "keyboard", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "kubernetesNode", "kubernetesPod", "launch", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logPatternAnalysis", "logRateAnalysis", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoVulnerabilityManagement", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "minusInSquare", "mobile", "monitoringApp", "moon", "move", "namespace", "nested", "newChat", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "palette", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipeBreaks", "pipelineApp", "pipeNoBreaks", "pivot", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "plusInSquare", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "singleMetricViewer", "snowflake", "sortAscending", "sortDescending", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spaces", "spacesApp", "sparkles", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelineWithArrow", "timelionApp", "timeRefresh", "timeslider", "training", "transitionLeftIn", "transitionLeftOut", "transitionTopIn", "transitionTopOut", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "vulnerabilityManagementApp", "warning", "warningFilled", "alert", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenAlias", "tokenAnnotation", "tokenArray", "tokenBinary", "tokenBoolean", "tokenClass", "tokenCompletionSuggester", "tokenConstant", "tokenDate", "tokenDimension", "tokenElement", "tokenEnum", "tokenEnumMember", "tokenEvent", "tokenException", "tokenField", "tokenFile", "tokenFlattened", "tokenFunction", "tokenGeo", "tokenHistogram", "tokenInterface", "tokenIP", "tokenJoin", "tokenKey", "tokenKeyword", "tokenMethod", "tokenMetricCounter", "tokenMetricGauge", "tokenModule", "tokenNamespace", "tokenNested", "tokenNull", "tokenNumber", "tokenObject", "tokenOperator", "tokenPackage", "tokenParameter", "tokenPercolator", "tokenProperty", "tokenRange", "tokenRankFeature", "tokenRankFeatures", "tokenRepo", "tokenSearchType", "tokenShape", "tokenString", "tokenStruct", "tokenSymbol", "tokenTag", "tokenText", "tokenTokenCount", "tokenVariable", "tokenVectorDense", "tokenDenseVector", "tokenVectorSparse"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired,
      isDisabled: PropTypes.bool
    }).isRequired,
    helpText: PropTypes.node,
    formatting: PropTypes.shape({
      prefix: PropTypes.string,
      suffix: PropTypes.string,
      blockPrefix: PropTypes.string,
      blockSuffix: PropTypes.string,
      multiline: PropTypes.bool,
      replaceNext: PropTypes.string,
      prefixSpace: PropTypes.bool,
      scanFor: PropTypes.string,
      surroundWithNewlines: PropTypes.bool,
      orderedList: PropTypes.bool,
      trimFirst: PropTypes.bool
    }),
    editor: PropTypes.elementType
  }).isRequired),
  /** errors to bubble up */errors: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.any.isRequired, PropTypes.any.isRequired]).isRequired),
  /** callback triggered when parsing results are available */onParse: PropTypes.func,
  /** initial display mode for the editor */initialViewMode: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.any.isRequired]),
  /** array defining any drag&drop handlers */dropHandlers: PropTypes.arrayOf(PropTypes.shape({
    supportedFiles: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    accepts: PropTypes.func.isRequired,
    getFormattingForItem: PropTypes.func.isRequired
  }).isRequired),
  /**
       * Sets the placeholder of the textarea
       */
  placeholder: PropTypes.any,
  /**
       * Further extend the props applied to EuiMarkdownFormat
       */
  markdownFormatProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    /**
         * Determines the text size. Choose `relative` to control the `font-size` based on the value of a parent container.
         */
    textSize: PropTypes.any
  })
};
EuiMarkdownEditor.displayName = 'EuiMarkdownEditor';
try {
  EuiMarkdownEditor.__docgenInfo = {
    tags: {},
    filePath: '/app/packages/eui/src/components/markdown_editor/markdown_editor.tsx',
    description: '',
    displayName: 'EuiMarkdownEditor',
    methods: [],
    props: {
      value: {
        defaultValue: null,
        description: 'A markdown content',
        name: 'value',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        },
        declarations: [{
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onChange: {
        defaultValue: null,
        description: 'callback function when markdown content is modified',
        name: 'onChange',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: '(value: string) => void'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'eui/node_modules/@emotion/react/types/css-prop.d.ts',
          name: 'Attributes'
        }, {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'eui/node_modules/@emotion/react/types/css-prop.d.ts',
          name: 'Attributes'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      height: {
        defaultValue: {
          value: '250'
        },
        description: 'Sets the `height` in pixels of the editor/preview area or pass `full` to allow\n' + 'the EuiMarkdownEditor to fill the height of its container.\n' + 'When in `full` mode the vertical resize is not allowed.',
        name: 'height',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number | "full"'
        }
      },
      placeholder: {
        defaultValue: null,
        description: 'Sets the placeholder of the textarea',
        name: 'placeholder',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      readOnly: {
        defaultValue: null,
        description: 'Sets the current display mode to a read-only state. All editing gets resctricted.',
        name: 'readOnly',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      editorId: {
        defaultValue: null,
        description: "a unique ID to attach to the textarea. If one isn't provided, a random one\n" + 'will be generated',
        name: 'editorId',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      maxHeight: {
        defaultValue: {
          value: '500'
        },
        description: 'Sets the `max-height` in pixels of the editor/preview area.\n' + 'It has no effect when the `height` is set to `full`.',
        name: 'maxHeight',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      autoExpandPreview: {
        defaultValue: {
          value: 'true'
        },
        description: 'Automatically adjusts the preview height to fit all the content and avoid a scrollbar.',
        name: 'autoExpandPreview',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      parsingPluginList: {
        defaultValue: {
          value: 'getDefaultEuiMarkdownParsingPlugins()'
        },
        description: 'plugins to identify new syntax and parse it into an AST node',
        name: 'parsingPluginList',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'PluggableList<Settings>'
        }
      },
      processingPluginList: {
        defaultValue: {
          value: 'getDefaultEuiMarkdownProcessingPlugins()'
        },
        description: 'plugins to process the markdown AST nodes into a React nodes',
        name: 'processingPluginList',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'PluggableList<Settings>'
        }
      },
      uiPlugins: {
        defaultValue: {
          value: 'getDefaultEuiMarkdownUiPlugins()'
        },
        description: "defines UI for plugins' buttons in the toolbar as well as any modals or extra UI that provides content to the editor",
        name: 'uiPlugins',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiMarkdownEditorUiPlugin<any>[]'
        }
      },
      errors: {
        defaultValue: {
          value: '[]'
        },
        description: 'errors to bubble up',
        name: 'errors',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiMarkdownParseError[]'
        }
      },
      onParse: {
        defaultValue: null,
        description: 'callback triggered when parsing results are available',
        name: 'onParse',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(error: EuiMarkdownParseError, data: { messages: VFileMessage[]; ast: EuiMarkdownAstNode; }) => void'
        }
      },
      initialViewMode: {
        defaultValue: {
          value: "'editing' as const"
        },
        description: 'initial display mode for the editor',
        name: 'initialViewMode',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'MARKDOWN_MODE',
          value: [{
            value: '"editing"'
          }, {
            value: '"viewing"'
          }]
        }
      },
      dropHandlers: {
        defaultValue: {
          value: '[]'
        },
        description: 'array defining any drag&drop handlers',
        name: 'dropHandlers',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiMarkdownDropHandler[]'
        }
      },
      markdownFormatProps: {
        defaultValue: null,
        description: 'Further extend the props applied to EuiMarkdownFormat',
        name: 'markdownFormatProps',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Omit<EuiMarkdownFormatProps, "children" | "parsingPluginList" | "processingPluginList">'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + 'aria-label OR aria-labelledby must be set\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        },
        declarations: [{
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'eui/src/components/markdown_editor/markdown_editor.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      ref: {
        defaultValue: null,
        description: 'Allows getting a ref to the component instance.\n' + 'Once the component unmounts, React will set `ref.current` to `null`\n' + '(or call the ref with `null` if you passed a callback ref).\n' + '@see {@link https ://react.dev/learn/referencing-values-with-refs#refs-and-the-dom React Docs}',
        name: 'ref',
        parent: {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'RefAttributes'
        },
        declarations: [{
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'RefAttributes'
        }],
        required: false,
        type: {
          name: 'LegacyRef<EuiMarkdownEditorRef>'
        }
      }
    },
    extendedInterfaces: ['HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps', 'Attributes']
  };
} catch (e) {}